import React, {Component, ReactNode} from 'react';
import Breadcrumb from '../components/Breadcrumb';
import Icon from '../icons/Icon';
import {FormicaProps, withFormica} from '../helpers/withFormica';
import {dispatch, store} from '../redux/store';
import ComponentName from '../constant/ComponentName';
import FolderService from '../service/FolderService';
import LanguageUtil from '../util/LanguageUtil';
import MessageUtil from '../util/MessageUtil';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import CustomListService from '../service/CustomListService';
import {CustomListModel} from '../domain/custom-list/CustomListDomains';
import CustomListCreatorComponent from '../components/kyc/custom-list/CustomListCreatorComponent';
import CustomListTableComponent from '../components/kyc/custom-list/CustomListTableComponent';
import CustomSidebarLayout from '../components/custom/CustomSidebarLayout';
import EmptyComponent from '../components/custom/empty-components/EmptyComponent';
import CustomLoading from '../components/custom/CustomLoading';
import CustomEmptyComponent from '../components/custom/CustomEmptyComponent';
import ItemToolsMenu from '../components/ItemToolsMenu';
import FilterUtil from '../util/FilterUtil';
import {CustomFilter as FilterType, CustomFilterFields} from '../domain/ui/FilterDomains';
import {changePageFilter} from '../redux/actions/PageActions';
import {setSelectedItems} from '../redux/actions/ComponentActions';
import {connect, ConnectedProps} from 'react-redux';
import {query} from '@trlogic/client-js';
import _ from 'lodash';
import Filter = query.Filter;
import empty = query.empty;

class State {
  public name: string = '';
  public description: string = '';
  public lists: CustomListModel[] = [];
  public filteredLists: CustomListModel[] = [];
  public selectedLists: CustomListModel[] = [];
  public loading: boolean = false;
  public creating: boolean = false;
  public visible: boolean = false;
  public filter: Filter = empty();
  public uiFilter: FilterType | undefined = undefined;
}

class CustomListContainer extends Component<Props, State> {
  public state: State = new State();

  private readonly customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);

  public render(): ReactNode {
    const {name, description, lists, filteredLists, selectedLists, loading, creating, visible, filter} = this.state;
    const itemsIsSelected: boolean = selectedLists.length > 0;
    const hasActiveFilter: boolean = (this.state.uiFilter?.fields?.length ?? 0) > 0;

    return (
      <div className={'w-full h-full flex flex-col overflow-y-hidden'}>
        <Breadcrumb/>
        <div className={'p-5'}>
          <div className={'flex'}>
            <span
              className="w-[60px] h-[60px] min-h-[60px] min-w-[60px] 2xl:w-[80px] 2xl:h-[80px] 2xl:min-h-[80px] 2xl:min-w-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE] mr-1">
              <Icon icon={'customlisticon'} className={'text-f-purple'} size={32}/>
            </span>
            <div className={'flex flex-col justify-center pl-3'}>
              <span className={'text-[24px] 2xl:text-4xl font-normal'}>
                {LanguageUtil.get({id: 'common.label.customList', defaultMessage: 'Custom List'})}
              </span>
              <span className={'text-md font-normal !text-f-black-3'}>
                {LanguageUtil.get({
                  id: 'pages.customList.description',
                  defaultMessage: 'You can create your custom list',
                })}
              </span>
            </div>
          </div>
        </div>
        <div className={'flex flex-col overflow-y-hidden h-full w-full pb-5'}>
          <div className={'w-full h-full flex flex-col overflow-y-hidden px-5'}>
            {loading ? (
              <CustomLoading/>
            ) : lists.length === 0 ? (
              <EmptyComponent type={ComponentName.CUSTOM_LIST} onCreate={this.openCreate}/>
            ) : (
              <>
                <ItemToolsMenu
                  views={false}
                  countVisible={true}
                  totalCount={filteredLists.length}
                  importVisible={false}
                  exportVisible={false}
                  itemsIsSelected={itemsIsSelected}
                  componentLabel={LanguageUtil.get({id: 'common.label.customList', defaultMessage: 'Custom List'})}
                  component={ComponentName.CUSTOM_LIST}
                  componentId={'custom-list'}
                  createFunc={this.openCreate}
                  removeAllFunc={this.onRemoveAll}
                  onChangeFilter={this.onChangeFilter}
                  filter={filter}
                  defaultFilterDisabled={true}
                />
                {filteredLists.length > 0 ? (
                  <CustomListTableComponent
                    lists={filteredLists}
                    selectedLists={selectedLists}
                    loading={false}
                    onRowClick={this.onRowClick}
                    onDelete={this.onDelete}
                    onSelectionChange={this.onSelectionChange}
                  />
                ) : (
                  <div className={'flex w-full h-full items-center justify-center'}>
                    <CustomEmptyComponent
                      label={
                        hasActiveFilter
                          ? LanguageUtil.get({
                            id: 'pages.customList.emptyFilteredLists',
                            defaultMessage: 'No custom lists match the current filters.',
                          })
                          : LanguageUtil.get({
                            id: 'pages.customList.emptyLists',
                            defaultMessage: 'No custom lists found.',
                          })
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {visible && <div className={'fixed inset-0 bg-[#D9D9D94D] z-[40]'}></div>}
        <CustomSidebarLayout
          icon={'customlisticon'}
          title={LanguageUtil.get({
            id: 'pages.customList.createTitle',
            defaultMessage: 'Create Custom List',
          })}
          visible={visible}
          closable={false}
          fullViewport={true}
          onHide={this.closeCreate}
          content={
            <CustomListCreatorComponent
              name={name}
              description={description}
              loading={creating}
              onChangeName={(value) => this.setState({name: value})}
              onChangeDescription={(value) => this.setState({description: value})}
              onAdd={this.onAdd}
            />
          }
        />
      </div>
    );
  }

  public async componentDidMount(): Promise<void> {
    const {
      router: {location},
    } = this.props;
    await FolderService.calculate('WITHOUT_FOLDER', ComponentName.CUSTOM_LIST, location.pathname, store.getState().page);
    this.prepareFilterFields();
    await this.loadLists();
  }

  private openCreate = (): void => {
    this.setState({visible: true});
  };

  private closeCreate = (): void => {
    this.setState({visible: false, name: '', description: ''});
  };

  private prepareFilterFields = (): void => {
    const {changePageFilter} = this.props;
    const fields: CustomFilterFields[] = FilterUtil.getCustomListFields();
    const filterState = store.getState().page.filter;
    filterState.items = fields;
    filterState.filter.fields = [];
    filterState.filter.type = filterState.filter.type || 'ALL_MATCH';
    changePageFilter(filterState);
    this.setState({
      filter: FilterUtil.parse(filterState.filter),
      uiFilter: filterState.filter,
    });
  };

  private onChangeFilter = (filter: Filter): void => {
    if (_.isEqual(filter, this.state.filter)) {
      return;
    }
    const uiFilter: FilterType = store.getState().page.filter.filter;
    const filteredLists: CustomListModel[] = this.customListService.filterLists(this.state.lists, uiFilter);
    dispatch(setSelectedItems([]));
    this.setState({
      filter,
      uiFilter,
      filteredLists,
      selectedLists: [],
    });
  };

  private loadLists = async (): Promise<void> => {
    this.setState({loading: true});
    try {
      const lists: CustomListModel[] = (await this.customListService.getAll()) || [];
      const filteredLists: CustomListModel[] = this.customListService.filterLists(lists, this.state.uiFilter);
      dispatch(setSelectedItems([]));
      this.setState({lists, filteredLists, selectedLists: [], loading: false});
    } catch (e) {
      console.error(e);
      dispatch(setSelectedItems([]));
      this.setState({loading: false, lists: [], filteredLists: [], selectedLists: []});
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.loadFailed', defaultMessage: 'Failed to load custom lists'}),
      });
    }
  };

  private onAdd = async (): Promise<void> => {
    const {name, description} = this.state;
    if (name.trim().length === 0) {
      MessageUtil.warning({
        summary: LanguageUtil.get({id: 'pages.customList.nameRequired', defaultMessage: 'Name is required'}),
      });
      return;
    }

    const id: string = this.customListService.createSlugId(name);

    const {lists} = this.state;
    const isDuplicate: boolean = lists.some((list: CustomListModel) => list.id === id);
    if (isDuplicate) {
      MessageUtil.warning({
        summary: LanguageUtil.get({
          id: 'pages.customList.duplicateId',
          defaultMessage: 'A custom list with the same name already exists',
        }),
      });
      return;
    }

    this.setState({creating: true});
    try {
      await this.customListService.create({
        id,
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : undefined,
      });
      this.setState({
        creating: false,
        name: '',
        description: '',
        visible: false,
      });
      await this.loadLists();
    } catch (e) {
      console.error(e);
      this.setState({creating: false});
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.createFailed', defaultMessage: 'Failed to create custom list'}),
      });
    }
  };

  private onRowClick = (list: CustomListModel): void => {
    this.props.router.navigate(`/custom-list/${list.id}/individual`);
  };

  private onSelectionChange = (lists: CustomListModel[]): void => {
    this.setState({selectedLists: lists});
    dispatch(setSelectedItems(lists.map((list) => ({id: list.id, stage: 'DRAFT'}))));
  };

  private onDelete = async (list: CustomListModel): Promise<void> => {
    try {
      await this.customListService.delete(list.id);
      MessageUtil.success({
        summary: LanguageUtil.get({id: 'pages.customList.deleteSuccess', defaultMessage: 'Custom list deleted'}),
      });
      await this.loadLists();
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.deleteFailed', defaultMessage: 'Failed to delete custom list'}),
      });
    }
  };

  private onRemoveAll = async (): Promise<void> => {
    const {selectedLists} = this.state;
    if (selectedLists.length === 0) {
      return;
    }

    try {
      for (const list of selectedLists) {
        await this.customListService.delete(list.id);
      }
      MessageUtil.success({
        summary: LanguageUtil.get({id: 'pages.customList.deleteSuccess', defaultMessage: 'Custom list deleted'}),
      });
      await this.loadLists();
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.deleteFailed', defaultMessage: 'Failed to delete custom list'}),
      });
      await this.loadLists();
    }
  };
}

const connector = connect(null, {changePageFilter});
type Props = FormicaProps & ConnectedProps<typeof connector>;

export default withFormica(connector(CustomListContainer));
